import 'react-phone-number-input/style.css'
import { type FC, useEffect, useMemo, type KeyboardEvent } from 'react'
import { getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input/input'
import { getExampleNumber } from 'libphonenumber-js/max'
import examples from 'libphonenumber-js/examples.mobile.json'
import classNames from 'classnames'
import {
  CountryEnum,
  Dropdown,
  Error,
  Icon,
  type IDropdownItem,
  type ITooltipProps,
  Label,
  Typography,
} from 'shared/ui'
import {
  makeTextFieldElement,
  makeTextFieldHighlight,
  makeTextFieldIcon,
  TextField,
  TextFieldPlaceholder,
} from 'shared/ui/TextField'
import { CountryFlag } from 'shared/ui/CountryFlag/CountryFlag'
import { type ICountryDropdownItem } from 'shared/ui/PhoneNumberInput/store/types'
import { InputPhoneNumber } from 'shared/ui/TextField/Input/InputPhoneNumber'
import styles from './styles.module.scss'

type IPhoneNumberInputProps = {
  label?: string
  number: string
  setNumber: (value: string) => void
  setCountry: (value: IDropdownItem) => void
  item: ICountryDropdownItem
  items: ICountryDropdownItem[]
  numberError?: string | null
  numberRequestError?: string | null
  questionTooltipProps?: ITooltipProps
  triggerNumberValidation?: () => void
  hideLabel?: boolean
  hideError?: boolean
  loading?: boolean
  disabled?: boolean
  readOnly?: boolean
}

export const PhoneNumberInput: FC<IPhoneNumberInputProps> = ({
  label = 'Mobile phone number',
  number,
  setNumber,
  setCountry,
  item,
  items,
  numberError,
  numberRequestError,
  questionTooltipProps,
  triggerNumberValidation,
  hideLabel = false,
  hideError = false,
  loading = false,
  disabled = false,
  readOnly = false,
}) => {
  const isInternational = item.id !== CountryEnum.CA && item.id !== CountryEnum.US
  const placeholder = useMemo(() => {
    let nationalPlaceholder = ''

    switch (item.id) {
      case CountryEnum.US:
      case CountryEnum.CA:
        nationalPlaceholder = '(555) 800-8000'
        break
      case CountryEnum.GB:
        nationalPlaceholder = '5555 800800'
        break
      case CountryEnum.AU:
        nationalPlaceholder = '412 345 678'
        break
      default:
        nationalPlaceholder = getExampleNumber(item.id, examples)?.formatInternational() ?? ''
    }

    return nationalPlaceholder
  }, [item.id])

  const maxDigits = useMemo(() => {
    const example = getExampleNumber(item.id, examples)

    return example?.nationalNumber.length ?? 15
  }, [item.id])

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const allowedControlKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Home',
      'End',
    ]

    if (allowedControlKeys.includes(e.key)) return
    if (e.ctrlKey || e.metaKey) return

    if (/\d/.test(e.key)) {
      const input = e.currentTarget
      const digits = input.value.replace(/\D/g, '')

      if (digits.length >= maxDigits) e.preventDefault()
    }
  }

  useEffect(() => {
    if (!number) return

    const parse = parsePhoneNumber(number)
    if (!parse || parse.nationalNumber.length <= maxDigits) return

    const trimmedNumber = parse.nationalNumber.slice(0, maxDigits)

    setNumber(`+${parse.countryCallingCode}${trimmedNumber}`)
  }, [item.id])

  return (
    <>
      {!hideLabel && (
        <Label
          label={{
            text: label,
            className: styles.inputLabel,
          }}
          questionTooltipProps={questionTooltipProps}
        />
      )}
      <TextField
        className={classNames(styles.textField, {
          [styles.error]: (numberError || numberRequestError) && !hideError,
        })}
        Input={InputPhoneNumber}
        InputProps={{
          country: item.id,
          isInternational: isInternational,
          placeholder,
          onPhoneValueChange: setNumber,
          onKeyDown: handleKeyDown,
        }}
        variant='stroke'
        size='medium'
        value={number}
        onChange={setNumber}
        onBlur={triggerNumberValidation}
        error={!!numberRequestError}
        disabled={loading || disabled}
        readOnly={readOnly}
        leftActions={[
          makeTextFieldElement({
            element: (
              <div className={styles.leftContentNumber}>
                <Dropdown
                  className={classNames(styles.textField, {
                    [styles.error]: (numberError || numberRequestError) && !hideError,
                  })}
                  placement='bottom-start'
                  width={191}
                  margin={-28}
                  marginLeft={-12}
                  maxHeight={136}
                  disabled={disabled || readOnly}
                  items={items}
                  onChange={setCountry}
                  triggerComponent={() => (
                    <TextFieldPlaceholder
                      rightActions={[makeTextFieldIcon({ icon: 'chevronDown' })]}
                      disabled={loading || disabled}
                      placeholder={
                        item.iconL ? (
                          <Icon icon={item.iconL} />
                        ) : (
                          <CountryFlag country={item.id} height={17} />
                        )
                      }
                      variant='integrated'
                      size='small'
                      className={styles.countryPicker}
                    />
                  )}
                />
                <div className={styles.verticalDivider} />
                <Typography variant='text-input-field' ariaLabel='numberPrefix'>
                  {`+${getCountryCallingCode(item.id)}`}
                </Typography>
              </div>
            ),
          }),
        ]}
        mainActions={[makeTextFieldHighlight({ mode: 'error', highlight: !!numberError })]}
      />
      {!hideError && <Error type={'text'} error={numberError || numberRequestError} />}
    </>
  )
}
